import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { api, getTemplates, sendCommunication, verifyEmail } from "./api";
import { toast, useToast } from "@shared/ui/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { useCallback, useState } from "react";
import { downloadCSV } from "@shared/utils/utils";

export const useGetFieldForceSummary = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.summary(),
    () => api.dashboard.summary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetFieldForceData = (
  page: number,
  limit: number,
  search_data?: string,
  filters?: any,
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [...QueryKeyFactory.module.wx.fieldForce.getList(), search_data, filters],
    () => api.list.getList(remoteUtilityId, page, limit, search_data, filters),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useFieldForceExport = (
  remote_utility_id: string,
  search_data?: string,
  filters?: any,
) => {
  const [isExporting, setIsExporting] = useState(false);

  const exportCSV = useCallback(async () => {
    if (isExporting) return;

    setIsExporting(true);
    try {
      const blob = await api.list.exportData(
        remote_utility_id,
        undefined,
        undefined,
        search_data,
        filters,
      );

      toast({
        title: "Export Initiated",
        description: blob?.message,
        variant: "default",
      });
    } catch (error: any) {
      toast({
        title: `Export failed: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setTimeout(() => setIsExporting(false), 0);
    }
  }, [remote_utility_id, search_data, filters, isExporting]);

  return { exportCSV, isExporting };
};

export const useGetFieldForceDetails = (ff_no: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.view.getDetails(ff_no),
    () => api.view.getDetails(remoteUtilityId, ff_no),
    {
      enabled: !!ff_no && !!remoteUtilityId,
      staleTime: 0,
      refetchOnMount: true,
    },
  );
};

export const useGetFieldForceAssignments = (
  ff_no: string,
  page: number = 1,
  search_data?: string,
  filters?: any,
) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    // ✅ Include page and search in query key for proper caching
    [
      ...QueryKeyFactory.module.wx.fieldForce.view.getAssignments(ff_no),
      page,
      search_data || "",
      filters,
    ],
    () =>
      api.view.getAssignments(
        remoteUtilityId,
        ff_no,
        page,
        search_data,
        filters,
      ),
    {
      enabled: !!ff_no && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetFieldForceTransactions = (ff_no: string, page: number) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.view.getTransactions(ff_no, page),
    () => api.view.getTransactions(remoteUtilityId, ff_no, page),
    {
      enabled: !!ff_no && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetFieldForceActivityLogs = (ff_id: string) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.view.getActivityLogs(ff_id),
    () => api.view.getActivityLogs(remoteUtilityId, ff_id),
    {
      enabled: !!ff_id && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useUpdateFieldForceStatus = () => {
  const { toast } = useToast();
  return useSmartMutation((payload: any) => api.list.updateStatus(payload), {
    onSuccess: () => {
      // Invalidate all field force list queries to refresh data
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.wx.fieldForce.getList(),
      });

      // Also invalidate summary to reflect updated status counts
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.wx.fieldForce.summary(),
      });

      // Invalidate update status queries if they exist
      queryClient.refetchQueries({
        queryKey: QueryKeyFactory.module.wx.fieldForce.updateStatus(),
      });
      toast({
        title: "Field force status updated successfully!",
      });
    },
  });
};

export const useUpdateFieldForce = (ff_no: any) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.update.updateFieldForce(payload, ff_no),
    {
      onSuccess: (data) => {
        toast({
          title: "Field force updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: [
            "wx",
            "fieldForce",
            "getDetails",
            data.result.fieldForceNo,
          ],
        });

        // Invalidate all field force list queries to refresh data
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.wx.fieldForce.getList(),
        });

        // Also invalidate summary to reflect updated status counts
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.wx.fieldForce.summary(),
        });

        // Invalidate update status queries if they exist
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.wx.fieldForce.updateFieldForce(),
        });
      },
    },
  );
};

export const useGetFieldForceCertificates = (ff_id: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.view.getAllCertificates(ff_id),
    () => api.view.getAllCertificates(remoteUtilityId, ff_id),
    {
      enabled: !!ff_id && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useAddCertificates = (ff_id: any) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.create.addCertificates(payload, ff_id),
    {
      onSuccess: () => {
        toast({
          title: "Certificates added successfully!",
        });

        // Invalidate all field force list queries to refresh data
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.wx.fieldForce.view.getAllCertificates(ff_id),
        });
      },
    },
  );
};

export const useAddSkills = (ff_id: any) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.create.addSkills(payload, ff_id),
    {
      onSuccess: () => {
        toast({
          title: "Skills added successfully!",
        });

        // Invalidate all field force list queries to refresh data
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.wx.fieldForce.view.getAllSkills(ff_id),
        });
      },
      onError: (error, variables, context) => {
        toast({
          variant: "destructive",
          title: error.message.toString(),
        });
      },
    },
  );
};

export const useGetFieldForceSkills = (ff_id: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.view.getAllSkills(ff_id),
    () => api.view.getAllSkills(remoteUtilityId, ff_id),
    {
      enabled: !!ff_id && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetFieldForceAvailability = (ff_id: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    [...QueryKeyFactory.module.wx.fieldForce.view.getAllAvailability(), ff_id],
    () => api.view.getAllAvailability(remoteUtilityId, ff_id),
    {
      enabled: !!ff_id && !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetSkills = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.getSkills(),
    () => api.update.getSkills(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetCertificates = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.getCertificates(),
    () => api.update.getCertificates(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetAreas = () => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.getAreas(),
    () => api.update.getAreas(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useGetSubAreas = (code_list: any[]) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.wx.fieldForce.getSubAreas(code_list),
    () => api.update.getSubAreas(remoteUtilityId, code_list),
    {
      enabled: !!remoteUtilityId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
  );
};

export const useUpdateOtherDetails = (ff_no: string) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.update.updateOtherDetails(payload),
    {
      onSuccess: () => {
        toast({
          title: "Other details updated successfully!",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.wx.fieldForce.view.getDetails(ff_no),
        });
      },
    },
  );
};

export const useUpdateAvailability = (ff_no) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.update.updateAvailabilitySchedule(payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.wx.fieldForce.view.getAllAvailability(),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.wx.fieldForce.view.getDetails(ff_no),
        });
        QueryKeyFactory.module.wx.fieldForce.view.getDetails(ff_no);
        toast({
          title: "Availability updated successfully!",
        });
      },
    },
  );
};

export const useRates = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.rates(), searchQuery], // Use dedicated rates query key
    async () => {
      const response = await api.list.getRates(utilityId, searchQuery);
      return response.result;
    },
    {
      enabled: !!utilityId,
    },
  );
};

export const useChoices = (choice: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.wx.shared.choices(choice),
    async () => {
      const response = await api.list.getChoices(choice);
      return response;
    },
    {
      enabled: !!choice,
    },
  );
};

export const useUpdateFieldForceActiveStatus = (id, ffNo) => {
  const { toast } = useToast();
  return useSmartMutation(
    (payload: any) => api.update.updateFieldForceActiveStatus(id, payload),
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: ["wx", "fieldForce", "getDetails", ffNo],
        });
        queryClient.refetchQueries({
          queryKey: ["wx", "fieldForce", "list"],
        });
        toast({
          title: "Field force status updated successfully!",
        });
      },
    },
  );
};

export const useTemplates = ({
  channelType,
  remoteUtilityId,
}: {
  channelType: string;
  remoteUtilityId: string;
}) =>
  useSmartQuery(
    QueryKeyFactory.module.ux.messaging.templates(channelType),
    () => getTemplates(channelType, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!channelType,
    },
  );

export const useSendCommunication = (id: string) => {
  return useSmartMutation((data: any) => sendCommunication(data), {
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Communication sent successfully!",
        variant: "default",
      });
    },
  });
};

export const useVerifyEmail = () => {
  return useSmartMutation((payload: any) => verifyEmail(payload));
};
